/**
 * NRRBenchmarkView — unified NRR chart with grouped bars per company,
 * quartile color-coding, dashed bars for estimated values, and management commentary.
 */

import { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  CartesianGrid,
  Cell,
} from "recharts";
import type {
  PeerCompany,
  QuartileStats,
  NrrDataPoint,
  NrrCommentaryItem,
} from "../types/diagnostic";
import { getQuartile } from "../lib/benchmark";
import { ChevronDown, ChevronUp, MessageSquareQuote } from "lucide-react";

interface NRRBenchmarkViewProps {
  peers: PeerCompany[];
  quartileStats: QuartileStats;
  clientNrr: number | null;
  clientNrrEstimated?: boolean;
  clientNrrPeriod?: string;
  clientNrrMethodology?: string;
  clientNrrHistory?: NrrDataPoint[];
  clientName: string;
  subVertical: string;
  revenueTier: string;
  nrrCommentary?: NrrCommentaryItem[];
}

/** Quartile colors (1 = top). */
const Q_COLORS: Record<number, string> = {
  1: "#16a34a",
  2: "#2563eb",
  3: "#f59e0b",
  4: "#dc2626",
};

/** Opacity per period position (oldest = lightest, newest = full). */
const PERIOD_OPACITY = [0.35, 0.55, 0.75, 1.0];

export function NRRBenchmarkView({
  peers,
  quartileStats,
  clientNrr,
  clientNrrEstimated = false,
  clientNrrPeriod,
  clientNrrMethodology,
  clientNrrHistory,
  clientName,
  subVertical,
  revenueTier,
  nrrCommentary,
}: NRRBenchmarkViewProps) {
  const [commentaryOpen, setCommentaryOpen] = useState(false);

  /** Build flat bar data: one entry per company per period, sorted for display. */
  const { bars, periods, companies } = useMemo(() => {
    // Collect companies
    type Co = {
      ticker: string;
      name: string;
      history: NrrDataPoint[];
      isEstimated: boolean;
      methodology: string | null;
    };
    const cos: Co[] = [];

    for (const p of peers) {
      const history = p.nrrHistory?.length
        ? p.nrrHistory
        : [{ period: p.fiscal_period ?? "Latest", nrr_pct: p.nrr_pct }];
      cos.push({
        ticker: p.ticker,
        name: p.name,
        history,
        isEstimated: !!p.nrrMethodology,
        methodology: p.nrrMethodology ?? null,
      });
    }
    if (clientNrr !== null) {
      const history = clientNrrHistory?.length
        ? clientNrrHistory
        : [{ period: clientNrrPeriod ?? "Latest", nrr_pct: clientNrr }];
      cos.push({
        ticker: clientName,
        name: clientName,
        history,
        isEstimated: clientNrrEstimated,
        methodology: clientNrrMethodology ?? null,
      });
    }

    // Get the union of periods, take last 4
    const allPeriods = new Set<string>();
    for (const c of cos) for (const dp of c.history) allPeriods.add(dp.period);
    const sortedPeriods = Array.from(allPeriods).sort().slice(-4);

    // Sort companies by most recent NRR
    cos.sort((a, b) => {
      const al = a.history[a.history.length - 1]?.nrr_pct ?? 0;
      const bl = b.history[b.history.length - 1]?.nrr_pct ?? 0;
      return al - bl;
    });

    // Build chart rows: one per company with period columns
    const rows = cos.map((c) => {
      const row: Record<string, unknown> = {
        ticker: c.ticker,
        name: c.name,
        isEstimated: c.isEstimated,
        methodology: c.methodology,
      };
      for (const p of sortedPeriods) {
        const dp = c.history.find((h) => h.period === p);
        row[p] = dp ? dp.nrr_pct : null;
      }
      return row;
    });

    return { bars: rows, periods: sortedPeriods, companies: cos };
  }, [peers, clientNrr, clientNrrEstimated, clientNrrPeriod, clientNrrMethodology, clientNrrHistory, clientName]);

  /** Collect footnotes. */
  const footnotes = useMemo(() => {
    const notes: { ticker: string; methodology: string }[] = [];
    for (const c of companies) {
      if (c.methodology) notes.push({ ticker: c.ticker, methodology: c.methodology });
    }
    return notes;
  }, [companies]);

  if (peers.length === 0) {
    return (
      <div className="mck-card p-6">
        <h2 className="text-lg font-semibold text-text-primary mb-2">NRR Benchmark</h2>
        <p className="text-text-secondary text-sm">No peer data available.</p>
      </div>
    );
  }

  /** Get the quartile color for an NRR value. */
  function qColor(nrr: number): string {
    return Q_COLORS[getQuartile(nrr, quartileStats)];
  }

  return (
    <div className="mck-card p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">NRR Benchmark</h2>
          <p className="text-sm text-text-secondary mt-0.5">
            {subVertical === "All" ? "All subverticals" : subVertical}
            {revenueTier !== "All" ? ` · ${revenueTier}` : ""} · {peers.length} peers
            {periods.length > 1 ? ` · ${periods.length} periods` : ""}
          </p>
        </div>
        {clientNrr !== null && (
          <div className="text-right">
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white"
              style={{ backgroundColor: qColor(clientNrr) }}
            >
              {clientName}: {clientNrr}%
              {clientNrrPeriod ? ` (${clientNrrPeriod})` : ""}
               — Q{getQuartile(clientNrr, quartileStats)}
            </span>
            {clientNrrEstimated && (
              <p className="text-xs text-text-muted mt-1">Estimated — hover bars for methodology</p>
            )}
          </div>
        )}
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Peers" value={String(quartileStats.count)} />
        <StatCard label="Median" value={`${quartileStats.median.toFixed(1)}%`} />
        <StatCard label="Top quartile (Q1)" value={`\u2265 ${quartileStats.q3.toFixed(1)}%`} />
        <StatCard label="Bottom quartile (Q4)" value={`\u2264 ${quartileStats.q1.toFixed(1)}%`} />
        <StatCard label="Range" value={`${quartileStats.min.toFixed(0)}\u2013${quartileStats.max.toFixed(0)}%`} />
      </div>

      {/* SVG defs for dashed patterns (one per quartile) */}
      <svg width={0} height={0} style={{ position: "absolute" }}>
        <defs>
          {[1, 2, 3, 4].map((q) => (
            <pattern
              key={q}
              id={`est-q${q}`}
              patternUnits="userSpaceOnUse"
              width="6"
              height="6"
              patternTransform="rotate(45)"
            >
              <rect width="6" height="6" fill={Q_COLORS[q]} fillOpacity={0.15} />
              <line x1="0" y1="0" x2="0" y2="6" stroke={Q_COLORS[q]} strokeWidth="3" strokeOpacity={0.45} />
            </pattern>
          ))}
        </defs>
      </svg>

      {/* Grouped bar chart */}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={bars} margin={{ top: 10, right: 20, left: 0, bottom: 30 }} barCategoryGap="20%" barGap={1}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
            <XAxis dataKey="ticker" tick={{ fontSize: 11, fill: "#6b7280" }} interval={0} />
            <YAxis
              domain={[(d: number) => Math.max(80, Math.floor(d / 5) * 5 - 5), (d: number) => Math.ceil(d / 5) * 5 + 5]}
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickFormatter={(v: number) => `${v}%`}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const row = payload[0]?.payload;
                return (
                  <div className="bg-white border border-surface-border rounded-lg shadow-mck-md p-3 text-sm max-w-xs">
                    <p className="font-medium text-text-primary mb-1">{label}{row?.name && label !== row.name ? ` — ${row.name}` : ""}</p>
                    {payload.filter((p) => p.value != null && (p.value as number) > 0).map((p, i) => {
                      const nrr = p.value as number;
                      const q = getQuartile(nrr, quartileStats);
                      return (
                        <p key={i} className="text-text-secondary text-xs">
                          <span className="inline-block w-2 h-2 rounded-sm mr-1.5" style={{ backgroundColor: Q_COLORS[q] }} />
                          {p.name}: <span className="font-semibold">{nrr}%</span>
                          <span className="text-text-muted"> (Q{q})</span>
                        </p>
                      );
                    })}
                    {row?.isEstimated && row?.methodology && (
                      <p className="text-text-tertiary text-xs mt-1.5 border-t border-surface-borderLight pt-1.5">
                        <span className="font-medium">Methodology:</span> {row.methodology as string}
                      </p>
                    )}
                  </div>
                );
              }}
            />
            <ReferenceLine y={100} stroke="#9ca3af" strokeWidth={1} strokeDasharray="4 2" />
            {periods.map((period, periodIdx) => (
              <Bar key={period} dataKey={period} name={period} radius={[2, 2, 0, 0]} maxBarSize={22}>
                {bars.map((row, rowIdx) => {
                  const nrr = row[period] as number | null;
                  if (!nrr) return <Cell key={rowIdx} fill="transparent" />;
                  const q = getQuartile(nrr, quartileStats);
                  const isEst = row.isEstimated as boolean;
                  const opacity = PERIOD_OPACITY[PERIOD_OPACITY.length - periods.length + periodIdx] ?? 1;
                  return (
                    <Cell
                      key={rowIdx}
                      fill={isEst ? `url(#est-q${q})` : Q_COLORS[q]}
                      fillOpacity={isEst ? 1 : opacity}
                      stroke={isEst ? Q_COLORS[q] : "none"}
                      strokeWidth={isEst ? 1.5 : 0}
                      strokeDasharray={isEst ? "4 2" : "none"}
                    />
                  );
                })}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-text-tertiary flex-wrap">
        <span className="font-medium">Quartile:</span>
        {([1, 2, 3, 4] as const).map((q) => (
          <div key={q} className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: Q_COLORS[q] }} />
            Q{q}
          </div>
        ))}
        <span className="mx-1 text-surface-border">|</span>
        <span className="font-medium">Period opacity:</span>
        {periods.map((p, i) => (
          <span key={p} className="text-text-muted" style={{ opacity: PERIOD_OPACITY[PERIOD_OPACITY.length - periods.length + i] }}>
            {p}
          </span>
        ))}
        {companies.some((c) => c.isEstimated) && (
          <>
            <span className="mx-1 text-surface-border">|</span>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-sm inline-block border-2 border-dashed" style={{ borderColor: "#6b7280", backgroundColor: "#6b728020" }} />
              Estimated / implied
            </div>
          </>
        )}
      </div>

      {/* Footnotes */}
      {footnotes.length > 0 && (
        <div className="border-t border-surface-borderLight pt-4 space-y-1.5">
          <p className="text-xs font-medium text-text-tertiary uppercase tracking-wide">NRR Methodology Notes</p>
          {footnotes.map((fn, idx) => (
            <p key={idx} className="text-xs text-text-muted leading-relaxed">
              <span className="font-medium text-text-tertiary">{fn.ticker}:</span> {fn.methodology}
            </p>
          ))}
        </div>
      )}

      {/* Management Commentary */}
      {nrrCommentary && nrrCommentary.length > 0 && (
        <div className="border-t border-surface-borderLight pt-4">
          <button onClick={() => setCommentaryOpen(!commentaryOpen)} className="w-full flex items-center justify-between text-left">
            <h3 className="text-sm font-semibold text-text-primary">Management Commentary on NRR</h3>
            <span className="text-text-muted">{commentaryOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
          </button>
          {commentaryOpen && (
            <div className="mt-3 space-y-3 animate-fade-in">
              {nrrCommentary.map((item, idx) => (
                <div key={idx} className="flex items-start gap-2 bg-surface-secondary rounded-lg p-3 border border-surface-borderLight">
                  <MessageSquareQuote size={12} className="text-text-muted mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-text-secondary italic leading-relaxed">{item.snippet}</p>
                    <p className="text-xs text-text-muted mt-1">
                      — <span className="font-medium">{item.company}</span>
                      {item.speaker ? ` (${item.speaker})` : ""}
                      {item.source ? ` · ${item.source}` : ""}
                      {item.date ? ` · ${item.date}` : ""}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-surface-secondary rounded-lg p-3 text-center">
      <p className="text-xs text-text-tertiary uppercase tracking-wide">{label}</p>
      <p className="text-lg font-semibold text-text-primary mt-0.5">{value}</p>
    </div>
  );
}
